import { SplitFactoryProvider } from '@splitsoftware/splitio-react'
import React, { useEffect, useMemo } from 'react'

import { getIsSsg, logger, LogLevel } from '../helpers/utils'
import { initSplitRUMAgent } from '../services/experimentsService'
import { useAnonymousIdContext } from './AnonymousIdContextProvider'

const composeSdkConfig = (splitUserKey: string): SplitIO.IBrowserSettings => {
  const isStagingEnv = typeof window !== 'undefined' && window.location.hostname === 'staging.bwll.net'
  const authorizationKey =
    (isStagingEnv ? process.env.GATSBY_SPLIT_IO_SDK_KEY_STAGING : process.env.GATSBY_SPLIT_IO_SDK_KEY) ?? ''

  if (!splitUserKey) {
    logger('[Split] Anonymous ID does not exist.', LogLevel.ERROR)
  }

  if (!authorizationKey) {
    logger('[Split] Missing Split authorization key.', LogLevel.ERROR)
  }

  return {
    // See readme for setup - https://github.com/splitio/react-client#readme
    core: {
      authorizationKey,
      key: splitUserKey,
      trafficType: 'user',
    },
    storage: {
      type: 'LOCALSTORAGE',
      prefix: 'bwll',
    },
  }
}

// Using the SplitFactoryProvider component, MyApp component and all it's descendants will have access to the SDK functionality.
export const SplitProvider: React.ComponentType = ({ children }) => {
  // TODO: GROW-1976- Make "successful Segment script load" not a dependency to having Anonymous ID
  const { anonymousId, isTimedOut } = useAnonymousIdContext()

  // Wait for either the real anonymousId OR the timeout to fire before initializing Split
  // This prevents treatment flickering from using temporary keys
  const shouldInitializeSplit = Boolean(anonymousId) || isTimedOut

  const splitUserKey: string = useMemo(() => {
    if (getIsSsg()) return 'ssg-user'
    // AnonymousIdContext already returns 'anonymous' when timed out
    return anonymousId
  }, [anonymousId])

  const sdkConfig: SplitIO.IBrowserSettings = useMemo(() => composeSdkConfig(splitUserKey), [splitUserKey])

  useEffect(() => {
    if (shouldInitializeSplit) {
      initSplitRUMAgent(splitUserKey)
    }
  }, [shouldInitializeSplit, splitUserKey])

  // Don't initialize Split until we have the final user key to prevent treatment flickering
  if (!shouldInitializeSplit) {
    return <>{children}</>
  }

  return (
    <SplitFactoryProvider config={sdkConfig} updateOnSdkTimedout updateOnSdkUpdate updateOnSdkReady>
      {children}
    </SplitFactoryProvider>
  )
}
